import React, { useMemo, useState } from 'react'
import {
  CircularProgress,
  Grid,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Box
} from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { TenantEditSlice, UserDeleteSlice } from '../../../../reducers'
import { useStyles } from '../style'
import { LoadingButton } from '@mui/lab'
import { DeleteForever } from '@mui/icons-material'
import { useArkMode } from '../../../../hooks/useArkMode'
import UserTableRow from './table-user-row'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useConfirm } from 'material-ui-confirm'
import { BannerStatic } from '../../../../components'

export default function UsersTable ({
  userList,
  isLoading,
  handleUserClick,
  onListUsers,
  selectedUsers,
  setSelectedUsers
}) {
  const classes = useStyles()
  const confirm = useConfirm()
  const dispatch = useDispatch()

  const [deletingMultipleUsers, setDeletingMultipleUsers] = useState(false)

  const { isAdminMode } = useArkMode()
  const tenant = useSelector(TenantEditSlice.selectors.tenant)
  const { userPoolId, pk: tenantId } = tenant

  const numSelected = selectedUsers.length

  const selectedUsernames = useMemo(() => {
    return selectedUsers.map(u => u.username)
  }, [selectedUsers])

  const [adminRemoveUserBatch] = useEvent([{
    eventName: 'adminRemoveUserBatch',
    onSuccess: (payload) => {
      const { failed, success, warnings, url } = payload
      dispatch(UserDeleteSlice.actions.setResults({
        success,
        failed,
        warnings,
        url
      }))
      setDeletingMultipleUsers(false)
      BannerStatic.show({ label: 'Users were successfully deleted.' })
      setSelectedUsers([])
      onListUsers()
    },
    onError: (err) => {
      console.log(err)
      setDeletingMultipleUsers(false)
      BannerStatic.show({ label: 'An unexpected error ocurred while deleting the users.', isError: true })
    }
  }])

  const handleDeleteUsers = () => {
    confirm({
      title: 'Delete Users Permanently',
      description: 'Are you sure you\'d like to delete these users permanently?\nOnce this action has been completed, it cannot be undone.',
      confirmationText: 'Proceed',
      confirmationButtonProps: {
        id: 'delete-users-confirmation-button',
        'data-test': 'delete-users-confirmation-button'
      },
      cancellationText: 'Cancel',
      cancellationButtonProps: {
        id: 'delete-users-cancellation-button',
        'data-test': 'delete-users-cancellation-button'
      },
      dialogProps: {
        PaperProps: {
          id: 'delete-users-confirmation-modal',
          'data-test': 'delete-users-confirmation-modal'
        }
      },
      titleProps: {
        id: 'delete-users-confirmation-title',
        'data-test': 'delete-users-confirmation-title'
      }
    }).then(() => {
      setDeletingMultipleUsers(true)
      adminRemoveUserBatch.trigger({
        usernames: selectedUsernames,
        tenantId
      })
    }).catch(() => console.log('Cancelled delete users'))
  }

  const handleClick = (event, user) => {
    event.stopPropagation()
    const selectedIndex = selectedUsers.findIndex(u => u.username === user.username)

    let newSelected = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedUsers, user)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedUsers.slice(1))
    } else if (selectedIndex === selectedUsers.length - 1) {
      newSelected = newSelected.concat(selectedUsers.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedUsers.slice(0, selectedIndex),
        selectedUsers.slice(selectedIndex + 1)
      )
    }

    setSelectedUsers(newSelected)
  }

  const handleSelectAllFromList = () => {
    setSelectedUsers(curr => {
      return [
        ...curr,
        ...userList.filter(user => !curr.find(u => u.username === user.username))
      ]
    })
  }

  const handleUnselectAllFromList = () => {
    setSelectedUsers(curr => {
      return curr.filter(user => !userList.find(u => u.username === user.username))
    })
  }

  const isSelected = (username) => selectedUsernames.includes(username)

  const numSelectInsideUserList = selectedUsers.filter(user => userList.find(u => u.username === user.username)).length
  const totalUserList = userList.length
  const isAllUserListChecked = totalUserList > 0 && numSelectInsideUserList === totalUserList
  const isIndeterminate = numSelectInsideUserList > 0 && numSelectInsideUserList < totalUserList

  return (
    <TableContainer component={Paper}>
      {selectedUsers.length > 0 && (
        <>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox' />
                <TableCell>
                  Selected
                </TableCell>
                <TableCell>
                  Email
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ backgroundColor: '#F9F9F9', paddingBottom: 16 }}>
              <TableRow>
                <TableCell padding='none' colSpan={3}>
                  <Box
                    display='flex'
                    flex={1}
                    flexDirection='row'
                    justifyContent='space-between'
                    alignItems='center'
                    p={1}
                  >
                    <Typography>
                      ({numSelected} selected users)
                    </Typography>
                    <LoadingButton
                      variant='outlined'
                      color='error'
                      onClick={handleDeleteUsers}
                      loading={deletingMultipleUsers}
                      startIcon={<DeleteForever />}
                      data-test='delete-user-btn'
                    >
                      Delete all selected
                    </LoadingButton>
                  </Box>
                </TableCell>
              </TableRow>
              {selectedUsers.map((user) => {
                const isItemSelected = isSelected(user.username)
                const labelId = `enhanced-table-checkbox-${user.username}`

                return (
                  <TableRow key={user.username} hover sx={{ backgroundColor: '#F9FCFF' }}>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        color='primary'
                        checked={isItemSelected}
                        disabled={deletingMultipleUsers}
                        onChange={event => handleClick(event, user)}
                        inputProps={{
                          'aria-labelledby': labelId
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {user.givenName} {user.familyName}

                    </TableCell>
                    <TableCell>
                      {user.email}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <Box mb={4} />
        </>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding='checkbox'>
              <Checkbox
                color='primary'
                disabled={isLoading || deletingMultipleUsers}
                indeterminate={isIndeterminate}
                checked={isAllUserListChecked}
                onChange={isAllUserListChecked || isIndeterminate ? handleUnselectAllFromList : handleSelectAllFromList}
                inputProps={{
                  'aria-label': 'select all users'
                }}
              />
            </TableCell>
            <TableCell>
              Full name
            </TableCell>
            <TableCell>
              Email
            </TableCell>
            {isAdminMode && (
              <TableCell>
                Status
              </TableCell>
            )}
            {selectedUsers.length === 0 && (
              <TableCell>
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={isAdminMode ? 5 : 4}>
                <Grid
                  id='user-list-loading'
                  container
                  direction='row'
                  justifyContent='center'
                  alignItems='center'
                  classes={{
                    root: classes.loadingAnimation
                  }}
                >
                  <CircularProgress size={60} color='primary' />
                </Grid>
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !userList.length && (
            <TableRow>
              <TableCell colSpan={isAdminMode ? 5 : 4}>
                <Grid
                  id='user-list-empty'
                  container
                  direction='row'
                  justifyContent='center'
                  alignContent='center'
                  classes={{
                    root: classes.emptyList
                  }}
                >
                  <Typography variant='h5'> No users found! </Typography>
                </Grid>
              </TableCell>
            </TableRow>

          )}
          {!isLoading &&
            userList.length > 0 &&
            userList.map((user) => {
              const isTempUser = user.username === tenant?.tempUser?.username
              const isItemSelected = isSelected(user.username)

              return (
                <UserTableRow
                  key={user.username}
                  user={user}
                  hideStatusColumn={!isAdminMode}
                  hideDeleteColumn={selectedUsers.length > 0}
                  userPoolId={userPoolId}
                  tenantId={tenantId}
                  handleSelect={handleClick}
                  disableCheckBox={deletingMultipleUsers}
                  isSelected={isItemSelected}
                  handleClick={handleUserClick}
                  isTempUser={isTempUser}
                />
              )
            })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
